import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { DiagnosticsPanel } from '../common/DiagnosticsPanel';
import { enableScreenWakeLock, disableScreenWakeLock, getScreenWakeLockStatus, getDetailedWakeLockStatus } from '../../utils/screenWakeLock';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  ChevronLeft,
  RotateCcw,
  SkipBack,
  SkipForward,
  Shield,
  Cast,
  Lock,
  Unlock,
  SquareDashedBottom,
  RefreshCw,
  X,
  Sparkles,
  Activity,
  Copy,
  Check,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { Episode } from '../../types/iptv';
import { getProxiedStreamUrl, isHttpsContext } from '../../utils/streamProxy';
import { formatSeconds } from '../../utils/timeFormat';
import { CastModal, getLastTvPosition } from './CastModal';
import { TvLoadingOverlay } from './TvLoadingOverlay';
import { toggleVideoFullscreen, isVideoInFullscreen } from '../../utils/fullscreen';
import { useScreenLockTimeout } from '../../hooks/useScreenLockTimeout';
import { withListAccount } from '../../utils/managerDns';
import { remoteLog } from '../../utils/remoteLog';
import { apiClient } from '../../lib/apiClient';

// V9.1.6 — Filmes/séries: DIRETO primeiro, ORACLE de reserva (voltou). Desde 30/10 o provedor manda para o Google
// pedidos de navegador (User-Agent de Chrome) — no Chrome o direto não abre; a Oracle se apresenta como player.
// V9.2.3 (pedido do Mario, 03/11): SEM memória de falhas. Cada filme/episódio tenta SEMPRE direto na Fusion primeiro;
// se não abrir, só ELE vai pela Oracle naquela vez. Antes, 2 falhas deixavam tudo começando pela Oracle por 24 h.
function vodHostOf(u: string): string {
  try {
    return new URL(u).host.toLowerCase();
  } catch {
    return '';
  }
}

export const VodPlayer: React.FC = () => {
  const {
    currentMovie,
    currentSeries,
    currentEpisode,
    playEpisode,
    stopPlayback,
    showToast,
    isPlayerControlsVisible,
    setIsPlayerControlsVisible,
    pokePlayerControls,
    hidePlayerControlsNow,
    clearPlayerControlsTimer,
    setControlsAutoHideSuppressed,
    initialSeekSeconds,
    updateWatchHistory,
    currentProfile,
    getStreamServer,
    getStreamAccount,
    settings,
    setCurrentStreamRoute,
    series,
    loadSeriesEpisodes,
    isAdmin,
    authUser,
    isCasting,
    toggleWatched,
  } = useIPTV();
  // V8.5.1: botão de diagnóstico/telemetria só para a conta do desenvolvedor (MARIOZELI, admin)
  const canSeeTelemetry = !!isAdmin && String((authUser as any)?.username || '').trim().toLowerCase() === 'mariozeli';

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSavedTimeRef = useRef<number>(-1);

  // Marca como ASSISTIDO ao passar de 95% ou terminar — mesma regra do Stick: filme → o filme;
  // episódio → o episódio; último episódio da temporada → a temporada inteira. Uma vez por conteúdo.
  const markedWatchedIdRef = useRef<string | null>(null);
  const markCurrentAsWatchedRef = useRef<() => void>(() => {});
  markCurrentAsWatchedRef.current = () => {
    const currentId = currentMovie?.id || currentEpisode?.id;
    if (!currentId || markedWatchedIdRef.current === currentId) return;
    markedWatchedIdRef.current = currentId;
    if (currentMovie) {
      toggleWatched(currentMovie.id, false, undefined, true);
      return;
    }
    if (currentSeries && currentEpisode) {
      const seasonEps = (currentSeries.episodes || []).filter((ep) => ep.seasonNumber === currentEpisode.seasonNumber);
      const lastEpNumber = Math.max(0, ...seasonEps.map((ep) => ep.episodeNumber || 0));
      const isLastOfSeason = seasonEps.length > 0 && (currentEpisode.episodeNumber || 0) === lastEpNumber;
      if (isLastOfSeason) {
        // temporada inteira; se for a ÚLTIMA temporada, a série também (sai do "Continuar Assistindo";
        // volta sozinha se o provedor lançar temporada/episódio novo)
        const ids = seasonEps.map((ep) => ep.id);
        const maxSeason = Math.max(0, ...(currentSeries.episodes || []).map((ep) => ep.seasonNumber || 0));
        if ((currentEpisode.seasonNumber || 0) === maxSeason) ids.push(currentSeries.id);
        toggleWatched(ids, false, undefined, true);
      } else {
        toggleWatched(currentEpisode.id, false, undefined, true);
      }
    }
  };
  const hasAppliedInitialSeekRef = useRef<boolean>(false);

  // Diagnóstico técnico (mesmo sistema do LivePlayer, agora também em filmes/séries)
  const playCountRef = useRef<number>(0);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState<boolean>(false);
  const [diagnosticLogs, setDiagnosticLogs] = useState<string[]>([]);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const addDiagLog = useCallback((msg: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR', { hour12: false });
    const line = `[${timestamp}] ${msg}`;
    console.log(line);
    setDiagnosticLogs((prev) => [...prev.slice(-49), line]);
  }, []);

  // Padlock & Touch Lock state
  const [isTouchLocked, setIsTouchLocked] = useState(false);
  const [isUnlockUIVisible, setIsUnlockUIVisible] = useState(false);
  const [unlockTapCount, setUnlockTapCount] = useState(0);
  const [lockedBannerText, setLockedBannerText] = useState<string | null>(null);
  const unlockUIRef = useRef<NodeJS.Timeout | null>(null);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUnlockClickRef = useRef<number>(0);
  const tapResetTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCastModalOpen, setIsCastModalOpen] = useState(false);
  const [isPipActive, setIsPipActive] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [aspectRatio, setAspectRatio] = useState<'contain' | 'cover' | 'fill'>('contain');

  // Encerramento automático de sessão após 60s com dispositivo bloqueado ou em background
  useScreenLockTimeout({
    enabled: !!(currentMovie || (currentSeries && currentEpisode)),
    isPipActive,
    timeoutSeconds: 60,
    onTimeout: stopPlayback,
  });

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedSeconds, setBufferedSeconds] = useState(0);
  const [candidateIndex, setCandidateIndex] = useState(0);
  const candidateIndexRef = useRef<number>(0);
  candidateIndexRef.current = candidateIndex;
  const [isNextEpCardDismissed, setIsNextEpCardDismissed] = useState(false);



  // Reseta dismiss do card quando o episódio mudar para reaparecer no próximo
  useEffect(() => {
    setIsNextEpCardDismissed(false);
  }, [currentEpisode?.id]);

  const seriesEpisodesFetchAttemptedRef = useRef<Set<string>>(new Set());

  // Se a série estiver sem os episódios completos carregados, busca no background (apenas 1 vez por série)
  useEffect(() => {
    if (currentSeries?.id && currentEpisode) {
      if (seriesEpisodesFetchAttemptedRef.current.has(currentSeries.id)) {
        return;
      }

      const hasRealEps =
        Array.isArray(currentSeries.episodes) &&
        currentSeries.episodes.length > 1 &&
        currentSeries.episodes.some(
          (e: any) => !e.id?.includes('-dummy') && e.title !== 'Carregar Episódios'
        );

      if (!hasRealEps && typeof loadSeriesEpisodes === 'function') {
        seriesEpisodesFetchAttemptedRef.current.add(currentSeries.id);
        loadSeriesEpisodes(currentSeries.id);
      }
    }
  }, [currentSeries?.id, currentSeries?.episodes, currentEpisode, loadSeriesEpisodes]);

  // Series: identificar próximo e episódio anterior com busca precisa por ID, streamUrl e ordenação
  const { nextEpisode, prevEpisode } = useMemo(() => {
    if (!currentSeries || !currentEpisode) {
      return { nextEpisode: null, prevEpisode: null };
    }

    // Prioriza episódios da série ativa ou busca da lista global carregada no contexto
    const contextSeries = series?.find((s) => s.id === currentSeries.id);
    const rawList =
      currentSeries.episodes && currentSeries.episodes.length > 1
        ? currentSeries.episodes
        : contextSeries?.episodes && contextSeries.episodes.length > 0
        ? contextSeries.episodes
        : currentSeries.episodes || [];

    const validEps = rawList.filter(
      (ep) => ep && !ep.id?.includes('-dummy') && ep.title !== 'Carregar Episódios'
    );

    if (validEps.length === 0) {
      return { nextEpisode: null, prevEpisode: null };
    }

    const parseNum = (val: any, fallback: number) => {
      if (val === undefined || val === null) return fallback;
      const parsed = parseInt(String(val), 10);
      return isNaN(parsed) ? fallback : parsed;
    };

    // Ordenação estrita: Temporada ASC, Episódio ASC
    const sortedEps = [...validEps].sort((a, b) => {
      const sA = parseNum(a.seasonNumber || (a as any).season, 1);
      const sB = parseNum(b.seasonNumber || (b as any).season, 1);
      if (sA !== sB) return sA - sB;
      const eA = parseNum(a.episodeNumber || (a as any).episode || (a as any).num, 0);
      const eB = parseNum(b.episodeNumber || (b as any).episode || (b as any).num, 0);
      return eA - eB;
    });

    // 1. Encontra o índice do episódio atual por ID
    let currentIdx = sortedEps.findIndex((ep) => ep.id === currentEpisode.id);

    // 2. Se não achar por ID, tenta por streamUrl
    if (currentIdx === -1 && currentEpisode.streamUrl) {
      currentIdx = sortedEps.findIndex((ep) => ep.streamUrl === currentEpisode.streamUrl);
    }

    // 3. Se ainda não achar, tenta por número de temporada e episódio
    const curS = parseNum(currentEpisode.seasonNumber || (currentEpisode as any).season, 1);
    const curE = parseNum(currentEpisode.episodeNumber || (currentEpisode as any).episode || (currentEpisode as any).num, 1);
    if (currentIdx === -1) {
      currentIdx = sortedEps.findIndex((ep) => {
        const s = parseNum(ep.seasonNumber || (ep as any).season, 1);
        const e = parseNum(ep.episodeNumber || (ep as any).episode || (ep as any).num, 1);
        return s === curS && e === curE;
      });
    }

    let nextEp: Episode | null = null;
    let prevEp: Episode | null = null;

    if (currentIdx !== -1) {
      nextEp = currentIdx < sortedEps.length - 1 ? sortedEps[currentIdx + 1] : null;
      prevEp = currentIdx > 0 ? sortedEps[currentIdx - 1] : null;
    } else {
      // Fallback: busca cronológica direta
      nextEp =
        sortedEps.find((ep) => {
          const s = parseNum(ep.seasonNumber || (ep as any).season, 1);
          const e = parseNum(ep.episodeNumber || (ep as any).episode || (ep as any).num, 1);
          return s > curS || (s === curS && e > curE);
        }) || null;

      prevEp =
        [...sortedEps].reverse().find((ep) => {
          const s = parseNum(ep.seasonNumber || (ep as any).season, 1);
          const e = parseNum(ep.episodeNumber || (ep as any).episode || (ep as any).num, 1);
          return s < curS || (s === curS && e < curE);
        }) || null;
    }

    return {
      prevEpisode: prevEp,
      nextEpisode: nextEp,
    };
  }, [currentSeries, currentEpisode, series]);

  // Active DNS hosts list for seamless multi-server failover
  // V9.0.2: DNS = a da lista do filme/série (perfil → conta → lista → DNS). Sempre 1. Nada fixo no código.
  const vodPlaylistId = (currentEpisode as any)?.playlistId || currentSeries?.playlistId || currentMovie?.playlistId || null;
  const streamServer = useMemo(() => getStreamServer(vodPlaylistId), [getStreamServer, vodPlaylistId]);
  // V9.0.6: conta atual da lista no endereço (o do catálogo pode ter conta antiga → 404 no direto)
  const streamAccount = useMemo(() => getStreamAccount(vodPlaylistId), [getStreamAccount, vodPlaylistId]);

  // Builds candidate streams: DIRECT Fusion first, Oracle VPS Proxy ONLY as fallback
  const buildCandidates = useCallback(
    (rawUrlIn: string, backupStreams?: string[]): { url: string; route: 'fusion' | 'oracle'; label: string }[] => {
      const rawUrl = withListAccount(rawUrlIn, streamAccount);
      let pathAndQuery = '';
      try {
        const u = new URL(rawUrl);
        pathAndQuery = u.pathname + u.search;
      } catch {
        pathAndQuery = rawUrl.replace(/^https?:\/\/[^\/]+/i, '');
      }

      const isHttps = isHttpsContext();
      const list: { url: string; route: 'fusion' | 'oracle'; label: string; resolver?: boolean }[] = [];

      // V9.0.2: 1 DNS (a da lista); sem lista no aparelho, a que veio no próprio item.
      let originHost = '';
      try {
        originHost = new URL(rawUrl).origin;
      } catch {}
      const server = streamServer || originHost;
      const servers = server ? [server] : [];

      // 1. PRIORIDADE 1: CONEXÃO DIRETA NO SERVIDOR (Sem proxy, consumo zero na Oracle)
      if (isHttps) {
        // Página HTTPS: tenta os servidores do Gerenciador em https (sem servidor fixo no código)
        servers.forEach((dns, idx) => {
          list.push({
            url: `${dns.replace(/^http:\/\//i, 'https://')}${pathAndQuery}`,
            route: 'fusion',
            label: `Fusion Direto SSL (DNS ${idx + 1})`,
          });
        });
        if (rawUrl.startsWith('https://')) {
          list.push({
            url: rawUrl,
            route: 'fusion',
            label: 'Fusion Direto (Original SSL)',
          });
        }
      } else {
        // V9.2.4: 1º o ENDEREÇO FINAL do vídeo. A porta de entrada da lista manda o navegador para o Google
        // (User-Agent de navegador), mas o servidor de vídeo para onde ela desvia aceita o navegador. O servidor do
        // site (api/vod_link.php) pede como player e devolve só o endereço; o vídeo vem direto, sem passar pela Oracle.
        if (/^\/(movie|series)\//i.test(pathAndQuery)) {
          servers.forEach((dns, idx) => {
            list.push({
              url: `${dns}${pathAndQuery}`,
              route: 'fusion',
              label: `Fusion Direto - endereço final (DNS ${idx + 1})`,
              resolver: true,
            });
          });
        }
        // Em Smart TV, Android TV ou ambiente HTTP, conecta direto em todos os nós DNS Fusion
        servers.forEach((dns, idx) => {
          list.push({
            url: `${dns}${pathAndQuery}`,
            route: 'fusion',
            label: `Fusion Direto (DNS ${idx + 1})`,
          });
        });
      }

      // V9.1.6: reserva pela Oracle, sempre DEPOIS do direto (V9.2.3: sem memória que punha a Oracle primeiro).
      const oracleList = servers.map((dns, idx) => ({
        url: getProxiedStreamUrl(`${dns}${pathAndQuery}`),
        route: 'oracle' as const,
        label: `Oracle (DNS ${idx + 1})`,
      }));
      list.push(...oracleList);

      // Endereços reserva do próprio item vão direto.
      if (backupStreams && backupStreams.length > 0) {
        backupStreams
          .filter((u) => !u.endsWith('.mkv') && !u.endsWith('.ts'))
          .forEach((bUrl) => {
            list.push({
              url: withListAccount(bUrl, streamAccount),
              route: 'fusion',
              label: 'Fusion Direto (Reserva do item)',
            });
          });
      }

      // Deduplica por URL mantendo a ordem estrita de prioridade
      const seen = new Set<string>();
      return list.filter((c) => {
        if (!c.url || seen.has(c.url)) return false;
        seen.add(c.url);
        return true;
      });
    },
    [streamServer, streamAccount]
  );

  // Content identification with multi-DNS stream candidates
  const contentInfo = useMemo(() => {
    if (currentMovie) {
      const is4k =
        currentMovie.quality === '4K' ||
        Boolean(currentMovie.has4K) ||
        (currentMovie.category && currentMovie.category.includes('4K')) ||
        currentMovie.title?.toUpperCase().includes('4K');

      const rawUrl = currentMovie.streamUrl;
      const candidates = buildCandidates(rawUrl, currentMovie.backupStreams);

      return {
        id: currentMovie.id,
        title: currentMovie.title,
        subtitle: currentMovie.year ? String(currentMovie.year) : currentMovie.category || 'Filme VOD',
        rawUrl,
        candidates,
        image: currentMovie.poster || currentMovie.backdrop,
        type: 'movie' as const,
        badge: is4k ? '4K ULTRA HD' : currentMovie.quality || 'FHD 1080p',
      };
    }
    if (currentSeries && currentEpisode) {
      const rawUrl = currentEpisode.streamUrl;
      const candidates = buildCandidates(rawUrl, currentEpisode.backupStreams);

      return {
        id: currentEpisode.id,
        title: currentSeries.title,
        subtitle: `T${currentEpisode.seasonNumber || 1}:E${currentEpisode.episodeNumber || 1} • ${currentEpisode.title || 'Episódio'}`,
        rawUrl,
        candidates,
        image: currentEpisode.thumbnail || currentSeries.poster,
        type: 'series' as const,
        badge: 'SÉRIE VOD',
      };
    }
    return null;
  }, [currentMovie, currentSeries, currentEpisode, buildCandidates]);

  // Reset candidate index when content switches
  useEffect(() => {
    setCandidateIndex(0);
  }, [contentInfo?.id]);

  // Active stream candidate
  const activeCandidate = useMemo(() => {
    if (!contentInfo || !contentInfo.candidates || contentInfo.candidates.length === 0) return null;
    return contentInfo.candidates[candidateIndex] || contentInfo.candidates[0] || null;
  }, [contentInfo, candidateIndex]);

  // V9.2.4: candidato "endereço final": pergunta ao servidor do site antes de tocar; se não vier, pula para o próximo
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const resolvingRef = useRef<string | null>(null);
  const streamUrl = (activeCandidate as any)?.resolver ? resolvedUrls[activeCandidate!.url] || '' : activeCandidate?.url || '';
  useEffect(() => {
    const cand: any = activeCandidate;
    if (!cand?.resolver || resolvedUrls[cand.url] || resolvingRef.current === cand.url) return;
    resolvingRef.current = cand.url;
    const idx = candidateIndexRef.current;
    const t0 = Date.now();
    apiClient.resolverVod(cand.url).then((final) => {
      if (resolvingRef.current === cand.url) resolvingRef.current = null;
      if (final) {
        addDiagLog(`[VOD] endereço final do vídeo em ${Date.now() - t0} ms (direto, sem Oracle)`);
        setResolvedUrls((m) => ({ ...m, [cand.url]: final }));
      } else {
        addDiagLog('[VOD] não veio o endereço final — tentando o próximo caminho');
        remoteLog('web_vod_direct_fail', `vod_link sem endereço | ${vodHostOf(cand.url)}`, 'info', { content_type: 'vod', content_name: contentInfo?.title || '' });
        if (candidateIndexRef.current === idx) setCandidateIndex(idx + 1);
      }
    });
  }, [activeCandidate, resolvedUrls, addDiagLog, contentInfo?.title]);

  // Cast: enquanto a TV toca, o celular solta o arquivo (só pausar mantinha o download/conexão aberta na
  // mesma conta do pool). Ao parar o Cast, recarrega na mesma posição (pausado).
  const castResumeAtRef = useRef<number | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isCasting) {
      if (castResumeAtRef.current === null) {
        castResumeAtRef.current = video.currentTime || 0;
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {}
      }
    } else if (castResumeAtRef.current !== null) {
      // V8.8.5: volta no ponto em que a TV parou (se era o mesmo filme/episódio) e não grava o ponto antigo do
      // celular por cima do progresso que a TV salvou no "Continuar Assistindo".
      const tvPos = getLastTvPosition();
      const resumeAt =
        tvPos && contentInfo && tvPos.contentId === contentInfo.id && tvPos.time > 0 ? tvPos.time : castResumeAtRef.current;
      castResumeAtRef.current = null;
      lastSavedTimeRef.current = resumeAt;
      if (streamUrl) {
        try {
          video.src = streamUrl;
          video.load();
          const onMeta = () => {
            try { video.currentTime = resumeAt; } catch {}
            video.removeEventListener('loadedmetadata', onMeta);
          };
          video.addEventListener('loadedmetadata', onMeta);
        } catch {}
      }
    }
  }, [isCasting, streamUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Synchronize current route with active candidate (fusion first, oracle fallback)
  useEffect(() => {
    if (activeCandidate) {
      setCurrentStreamRoute(activeCandidate.route);
    }
  }, [activeCandidate?.route, setCurrentStreamRoute]);

  const getFullDiagnosticReport = useCallback(() => {
    const video = videoRef.current;
    const bufEnd =
      video && video.buffered.length > 0
        ? video.buffered.end(video.buffered.length - 1)
        : 0;
    const wlDetails = getDetailedWakeLockStatus();
    const urlLower = (streamUrl || '').toLowerCase();
    const detectedFormat = urlLower.includes('.m3u8')
      ? 'HLS (.m3u8)'
      : urlLower.includes('.ts') || urlLower.includes('output=ts')
      ? 'MPEG-TS'
      : 'Desconhecido/outro';
    const decodedFrames = (video as any)?.webkitDecodedFrameCount;
    const droppedFrames = (video as any)?.webkitDroppedFrameCount;

    return [
      `=== RELATÓRIO DE DIAGNÓSTICO IPTV (VOD) ===`,
      `Título: ${contentInfo?.title || 'N/A'} (ID: ${contentInfo?.id || 'N/A'}, Tipo: ${contentInfo?.type || 'N/A'})`,
      `Dispositivo: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}`,
      `Formato Detectado na URL: ${detectedFormat}`,
      `Rota Ativa: ${activeCandidate?.route || 'padrão'} (candidato ${candidateIndexRef.current + 1}/${contentInfo?.candidates?.length || 1})`,
      `URL do Stream (bruta): ${streamUrl ? `"${streamUrl.substring(0, 80)}..." (${streamUrl.length} chars)` : 'VAZIO — nada será carregado!'}`,
      `Tempo Vídeo: ${video ? video.currentTime.toFixed(2) : '0.00'}s / ${duration ? duration.toFixed(2) : '0.00'}s`,
      `Dimensões Vídeo (videoWidth x videoHeight): ${video ? `${video.videoWidth}x${video.videoHeight}` : 'N/A'}`,
      `Frames Decodificados (WebKit): ${decodedFrames !== undefined ? decodedFrames : 'N/A'}`,
      `Frames Descartados (WebKit): ${droppedFrames !== undefined ? droppedFrames : 'N/A'}`,
      `ReadyState: ${video ? video.readyState : 'N/A'}`,
      `NetworkState: ${video ? video.networkState : 'N/A'}`,
      `Pausado: ${video?.paused ? 'SIM' : 'NAO'}`,
      `Buffer Final: ${bufEnd.toFixed(2)}s (diff: ${video ? (bufEnd - video.currentTime).toFixed(2) : '0'}s)`,
      `Chamadas play(): ${playCountRef.current}`,
      `WakeLock Tela: ${getScreenWakeLockStatus()}`,
      `WakeLock Suportado: ${wlDetails.wakeLockSupported ? 'SIM' : 'NAO'}`,
      `WakeLock Erro: ${wlDetails.lastWakeLockError}`,
      `Detalhes WakeLock: ${JSON.stringify(wlDetails)}`,
      ``,
      `--- LOGS DE EVENTOS ---`,
      ...diagnosticLogs,
    ].join('\n');
  }, [contentInfo, diagnosticLogs, streamUrl, duration, activeCandidate]);

  const copyDiagnosticReport = useCallback(() => {
    const reportText = getFullDiagnosticReport();
    let copied = false;
    try {
      const textArea = document.createElement('textarea');
      textArea.value = reportText;
      textArea.style.position = 'fixed';
      textArea.style.left = '0';
      textArea.style.top = '0';
      textArea.style.opacity = '0.01';
      textArea.setAttribute('readonly', '');
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, 99999);
      copied = document.execCommand('copy');
      document.body.removeChild(textArea);
    } catch {
      copied = false;
    }
    if (!copied && navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(reportText).catch(() => {});
      copied = true;
    }
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
    showToast('📋 Diagnóstico copiado para a área de transferência!', 'success');
  }, [getFullDiagnosticReport, showToast]);

  // Safe play helper com destravamento de autoplay
  const safePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    playCountRef.current += 1;
    addDiagLog(`[VOD] play() chamado (#${playCountRef.current})`);
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        if (err.name === 'NotAllowedError') {
          // Autoplay desmutado bloqueado pelo navegador: inicia mutado para rodar imediatamente
          video.muted = true;
          video.play().then(() => {
            setTimeout(() => {
              if (video && !isMutedRef.current) {
                video.muted = false;
              }
            }, 300);
          }).catch(() => {});
        } else if (err.name !== 'AbortError') {
          console.warn('[VodPlayer] Autoplay bloqueado pelo navegador:', err.message);
        }
      });
    }
  }, [addDiagLog]);
  const requestWakeLock = useCallback(() => {
    enableScreenWakeLock();
  }, []);

  const releaseWakeLock = useCallback(() => {
    disableScreenWakeLock();
  }, []);

  // Detecta se o app está rodando "instalado" (atalho na Tela de Início / modo standalone)
  // O WebKit do iOS desativa o Picture-in-Picture nesse modo, mesmo funcionando normalmente no Safari.
  // Referência: https://discussions.apple.com/thread/255709452
  const isStandaloneIOS = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const isStandaloneDisplay =
      (window as any).navigator?.standalone === true ||
      window.matchMedia?.('(display-mode: standalone)')?.matches === true;
    return isStandaloneDisplay && /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);


  // Adiciona listeners para capturar o toque/clique do usuário e manter a tela permanentemente acesa
  useEffect(() => {
    const onUserGesture = () => {
      enableScreenWakeLock();
    };

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        enableScreenWakeLock();
      }
    };

    window.addEventListener('touchstart', onUserGesture, { passive: true, capture: true });
    window.addEventListener('touchend', onUserGesture, { passive: true, capture: true });
    window.addEventListener('click', onUserGesture, { passive: true, capture: true });
    window.addEventListener('pointerdown', onUserGesture, { passive: true, capture: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    enableScreenWakeLock();

    return () => {
      window.removeEventListener('touchstart', onUserGesture, { capture: true });
      window.removeEventListener('touchend', onUserGesture, { capture: true });
      window.removeEventListener('click', onUserGesture, { capture: true });
      window.removeEventListener('pointerdown', onUserGesture, { capture: true });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disableScreenWakeLock();
    };
  }, []);

  // Action: Switch directly to Next Episode with clean video reload
  const handlePlayNextEpisode = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!currentSeries || !nextEpisode) return;

      setIsNextEpCardDismissed(true);
      setCurrentTime(0);
      setBufferedSeconds(0);
      setDuration(0);
      setCandidateIndex(0);
      hasAppliedInitialSeekRef.current = false;
      lastSavedTimeRef.current = 0;

      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {}
      }

      playEpisode(currentSeries, nextEpisode, true);
    },
    [currentSeries, nextEpisode, playEpisode]
  );

  // Action: Switch directly to Prev Episode with clean video reload
  const handlePlayPrevEpisode = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!currentSeries || !prevEpisode) return;

      setIsNextEpCardDismissed(true);
      setCurrentTime(0);
      setBufferedSeconds(0);
      setDuration(0);
      setCandidateIndex(0);
      hasAppliedInitialSeekRef.current = false;
      lastSavedTimeRef.current = 0;

      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {}
      }

      playEpisode(currentSeries, prevEpisode, true);
    },
    [currentSeries, prevEpisode, playEpisode]
  );

  // Initialize playback and seek position
  useEffect(() => {
    if (!contentInfo || !streamUrl || !videoRef.current) return;

    addDiagLog(`[VOD] load contentId=${contentInfo.id} title="${contentInfo.title}" tipo=${contentInfo.type} rota=${activeCandidate?.route || 'padrão'}`);
    addDiagLog(`[VOD] streamUrl: ${streamUrl ? `"${streamUrl.substring(0, 80)}..." (${streamUrl.length} chars)` : 'VAZIO — nada será carregado!'}`);

    setHasError(false);
    setIsBuffering(true);
    setErrorMessage('');
    setCurrentTime(0);
    setBufferedSeconds(0);
    hasAppliedInitialSeekRef.current = false;
    setIsNextEpCardDismissed(false);

    const video = videoRef.current;
    if (video.src !== streamUrl) {
      video.src = streamUrl;
    }
    safePlay();

    // Check resume position (respeita 0 explicitamente ao iniciar novo episódio)
    let targetSeek = -1;
    if (initialSeekSeconds !== null && initialSeekSeconds !== undefined) {
      targetSeek = initialSeekSeconds;
    } else if (currentProfile?.watchHistory) {
      const saved = currentProfile.watchHistory.find((h) => h.contentId === contentInfo.id);
      if (
        saved &&
        saved.watchedSeconds > 10 &&
        (saved.totalSeconds === 0 || saved.watchedSeconds < saved.totalSeconds - 20)
      ) {
        targetSeek = Math.floor(saved.watchedSeconds);
      }
    }
    if (targetSeek < 0) targetSeek = 0;

    const applySeek = () => {
      if (!hasAppliedInitialSeekRef.current && targetSeek > 0 && video.duration) {
        try {
          video.currentTime = Math.min(targetSeek, video.duration - 2);
          hasAppliedInitialSeekRef.current = true;
          showToast(`Retomando em ${formatSeconds(targetSeek)}`, 'info');
        } catch {}
      }
      if (video.paused) {
        safePlay();
      }
    };

    video.addEventListener('loadedmetadata', applySeek, { once: true });
    video.addEventListener('canplay', applySeek, { once: true });

    return () => {
      video.removeEventListener('loadedmetadata', applySeek);
      video.removeEventListener('canplay', applySeek);
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {}
    };
  }, [contentInfo?.id, streamUrl, safePlay, addDiagLog, activeCandidate]);

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateBuffered = () => {
      try {
        if (video.buffered && video.buffered.length > 0) {
          const cur = video.currentTime;
          for (let i = video.buffered.length - 1; i >= 0; i--) {
            if (video.buffered.start(i) <= cur) {
              setBufferedSeconds(video.buffered.end(i));
              return;
            }
          }
          setBufferedSeconds(video.buffered.end(video.buffered.length - 1));
        }
      } catch {}
    };

    const onTimeUpdate = () => {
      const cur = video.currentTime;
      setCurrentTime(cur);
      updateBuffered();

      // Se o tempo estiver progredindo, cancela buffering imediatamente
      if (cur > 0 && !video.paused) {
        setIsBuffering(false);
        setHasError(false);
        stallCount = 0;
      }

      if (contentInfo && Math.abs(cur - lastSavedTimeRef.current) > 10) {
        lastSavedTimeRef.current = cur;
        updateWatchHistory(contentInfo.id, contentInfo.type, cur, video.duration || 0);
      }
      if (video.duration > 0 && cur / video.duration >= 0.95) {
        markCurrentAsWatchedRef.current();
      }
    };

    const onDurationChange = () => {
      if (video.duration && !isNaN(video.duration)) {
        setDuration(video.duration);
      }
    };

    let lastCurrentTime = video.currentTime;
    let stallCount = 0;

    const onPlaying = () => {
      addDiagLog(`[VOD] event=playing currentTime=${video.currentTime.toFixed(2)}`);
      setIsPlaying(true);
      setIsBuffering(false);
      setHasError(false);
      stallCount = 0;
      requestWakeLock();
      if (activeCandidate?.route) {
        setCurrentStreamRoute(activeCandidate.route);
      }
    };

    const onWaiting = () => {
      addDiagLog(`[VOD] event=waiting currentTime=${video.currentTime.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState}`);
      if (video.currentTime === 0 || video.paused) {
        setIsBuffering(true);
      }
    };

    const onCanPlay = () => {
      addDiagLog(`[VOD] event=canplay currentTime=${video.currentTime.toFixed(2)} readyState=${video.readyState}`);
      setIsBuffering(false);
      setHasError(false);
      if (video.paused) {
        safePlay();
      }
    };

    const onPause = () => {
      setIsPlaying(false);
    };

    // No iOS, se houver stall no streaming HTTP, força pequena recuperação
    const onStalled = () => {
      addDiagLog(`[VOD] event=stalled currentTime=${video.currentTime.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState}`);
      if (!video.paused && !video.ended) {
        if (video.currentTime === 0) {
          setIsBuffering(true);
        }
        try {
          safePlay();
        } catch {}
      }
    };

    const onEnded = () => {
      addDiagLog(`[VOD] event=ended currentTime=${video.currentTime.toFixed(2)}`);
      setIsPlaying(false);
      markCurrentAsWatchedRef.current();
      if (nextEpisode) {
        showToast(`Próximo episódio: ${nextEpisode.title || `T${nextEpisode.seasonNumber}E${nextEpisode.episodeNumber}`}`, 'info');
        handlePlayNextEpisode();
      }
    };

    const onError = () => {
      const video = videoRef.current;
      if (!video) return;
      const mediaErr = video.error;

      addDiagLog(`[VOD] event=error code=${mediaErr?.code} message="${mediaErr?.message || ''}"`);

      // Código 1: MEDIA_ERR_ABORTED - cancelamento normal pelo navegador
      if (mediaErr && mediaErr.code === 1) {
        return;
      }

      console.warn('[VodPlayer] Falha no fluxo do candidato', candidateIndexRef.current, streamUrl, mediaErr?.code, mediaErr?.message);

      const candidates = contentInfo?.candidates || [];
      const nextIdx = candidateIndexRef.current + 1;
      const failedCand = candidates[candidateIndexRef.current];
      // V9.2.3: motivo do direto ter falhado vai para o servidor (Gerenciador / system_telemetry_logs: web_vod_direct_fail)
      if (failedCand?.route === 'fusion') {
        remoteLog('web_vod_direct_fail', `erro ${mediaErr?.code ?? '?'} ${mediaErr?.message || ''} | ${vodHostOf(failedCand.url)}`, 'info', {
          content_type: 'vod',
          content_name: contentInfo?.title || '',
        });
      }
      if (nextIdx < candidates.length) {
        addDiagLog(`[VOD] tentando próximo candidato (${nextIdx + 1}/${candidates.length}, rota=${candidates[nextIdx]?.route || 'padrão'})`);
        setCandidateIndex(nextIdx);
        setIsBuffering(true);
        const nextCand = candidates[nextIdx];
        if (nextCand?.route === 'oracle') {
          showToast('Conectando via Servidor Reserva Oracle...', 'info');
        } else {
          showToast(`Tentando servidor alternativo Fusion (${nextIdx + 1}/${candidates.length})...`, 'info');
        }
        return;
      }

      addDiagLog(`[VOD] sem mais candidatos — reprodução falhou definitivamente.`);
      setIsBuffering(false);
      setHasError(true);
      if (contentInfo?.badge?.includes('4K') || (contentInfo?.title && contentInfo.title.toUpperCase().includes('4K'))) {
        setErrorMessage('Este vídeo está codificado em 4K HEVC (H.265). O navegador atual não possui codec HEVC instalado. Este conteúdo reproduzirá com aceleração de hardware nativa no APK Android, TV Box ou Smart TV.');
      } else {
        setErrorMessage('Não foi possível reproduzir este vídeo nos servidores disponíveis. Verifique sua conexão.');
      }
    };

    // Watchdog de reprodução VOD para iOS: se o vídeo não estiver pausado e o currentTime congelar
    const watchdogInterval = setInterval(() => {
      if (!video || video.paused || video.ended || hasError) {
        stallCount = 0;
        return;
      }

      const cur = video.currentTime;
      if (Math.abs(cur - lastCurrentTime) < 0.1) {
        stallCount++;
        addDiagLog(`[VOD] watchdog-stall stallCount=${stallCount} cur=${cur.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState}`);
        // Se permanecer travado por mais de 2 checagens consecutivas (7 segundos)
        if (stallCount >= 2) {
          if (video.readyState === 0) {
            addDiagLog(`[VOD] watchdog: readyState=0 após ${stallCount} checagens (servidor inacessível) — avançando para próximo candidato`);
            onError();
            return;
          }
          setIsBuffering(true);
          try {
            // No iOS/Safari, um micro-seek de 0.05s força o motor a pedir novos bytes sem reiniciar o filme
            video.currentTime = cur + 0.05;
            safePlay();
          } catch {}
        }
      } else {
        stallCount = 0;
        lastCurrentTime = cur;
        setIsBuffering(false);
      }
    }, 3500);

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('progress', updateBuffered);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('canplay', () => { onCanPlay(); updateBuffered(); });
    video.addEventListener('pause', onPause);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);

    return () => {
      clearInterval(watchdogInterval);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('progress', updateBuffered);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [contentInfo, nextEpisode, currentSeries, playEpisode, updateWatchHistory, showToast, activeCandidate, setCurrentStreamRoute, safePlay, hasError, requestWakeLock, addDiagLog]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTouchLocked) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          togglePlay();
          pokePlayerControls();
          break;
        case 'ArrowLeft':
        case 'Left':
          e.preventDefault();
          if (videoRef.current) {
            const newPos = Math.max(0, videoRef.current.currentTime - 10);
            videoRef.current.currentTime = newPos;
            setCurrentTime(newPos);
            showToast(`⏪ -10s (${formatSeconds(newPos)})`, 'info');
          }
          pokePlayerControls();
          break;
        case 'ArrowRight':
        case 'Right':
          e.preventDefault();
          if (videoRef.current) {
            const newPos = Math.min(duration || 100, videoRef.current.currentTime + 10);
            videoRef.current.currentTime = newPos;
            setCurrentTime(newPos);
            showToast(`⏩ +10s (${formatSeconds(newPos)})`, 'info');
          }
          pokePlayerControls();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Escape':
        case 'Back':
        case 'BrowserBack':
          e.preventDefault();
          stopPlayback();
          break;
        default:
          pokePlayerControls();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTouchLocked, duration, stopPlayback, pokePlayerControls, showToast]);

  // Fullscreen listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(isVideoInFullscreen(containerRef.current));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const [isScrubbing, setIsScrubbing] = useState(false);

  // Controls auto-hide — delega para a fonte única (IPTVContext) que também atende
  // controle remoto/teclado, evitando dois cronômetros concorrentes.
  const lastTouchTimeRef = useRef<number>(0);
  const touchStartPosRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastToggleTimeRef = useRef<number>(0);

  const handleUserActivity = useCallback(() => {
    requestWakeLock();
    if (isTouchLocked || isScrubbing) return;
    // Ignora eventos mousemove sintéticos gerados pelo navegador logo após toques na tela (Android/iOS)
    if (Date.now() - lastTouchTimeRef.current < 1000) return;
    pokePlayerControls();
  }, [isTouchLocked, isScrubbing, pokePlayerControls, requestWakeLock]);

  // Impede o auto-hide de disparar durante buffering (ex: filme/série 4K) ou enquanto o usuário
  // arrasta a barra de progresso — reaplica sempre que qualquer uma das duas mudar.
  useEffect(() => {
    setControlsAutoHideSuppressed(isBuffering || isScrubbing);
  }, [isBuffering, isScrubbing, setControlsAutoHideSuppressed]);

  // Padlock: Banner & Temporarily show discrete unlock button
  const showLockedBanner = useCallback((text: string) => {
    setLockedBannerText(text);
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    bannerTimeoutRef.current = setTimeout(() => setLockedBannerText(null), 2200);
  }, []);

  const showUnlockButtonTemporarily = useCallback(() => {
    setIsUnlockUIVisible(true);
    if (unlockUIRef.current) clearTimeout(unlockUIRef.current);
    unlockUIRef.current = setTimeout(() => {
      setIsUnlockUIVisible(false);
      setUnlockTapCount(0);
    }, 3500);
  }, []);

  // Alternar visibilidade dos controles ao tocar na tela / vídeo
  const handleToggleControls = useCallback(
    (e?: React.SyntheticEvent | React.TouchEvent | React.MouseEvent) => {
      requestWakeLock();
      if (e && 'stopPropagation' in e) e.stopPropagation();

      // Trava anti-duplo clique: ignora eventos repetidos em menos de 400ms (ex: touchend seguido de click)
      if (Date.now() - lastToggleTimeRef.current < 400) return;
      lastToggleTimeRef.current = Date.now();

      if (isTouchLocked) {
        showUnlockButtonTemporarily();
        return;
      }
      // Se estiver carregando, mantém visível para permitir voltar ou destravar
      if (isPlayerControlsVisible && !isBuffering) {
        hidePlayerControlsNow();
      } else {
        pokePlayerControls(5000);
      }
    },
    [isTouchLocked, isPlayerControlsVisible, isBuffering, showUnlockButtonTemporarily, pokePlayerControls, hidePlayerControlsNow, requestWakeLock]
  );

  // 1 Clique Simples para Travar
  const handleLockScreen = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) e.stopPropagation();
      setIsTouchLocked(true);
      setIsPlayerControlsVisible(false);
      setIsUnlockUIVisible(false);
      setUnlockTapCount(0);
      showLockedBanner('TELA BLOQUEADA 🔒');
      showToast('Tela Bloqueada 🔒', 'info');

      // Tenta travar rotação em landscape se disponível
      if (typeof window !== 'undefined' && window.screen && (window.screen.orientation as any)?.lock) {
        (window.screen.orientation as any).lock('landscape').catch(() => {});
      }
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate(50);
        } catch {}
      }
    },
    [setIsPlayerControlsVisible, showLockedBanner, showToast]
  );

  // 2 Cliques Rápidos (Duplo Toque) para Destravar
  const handleUnlockTap = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) e.stopPropagation();
      const now = Date.now();
      const diff = now - lastUnlockClickRef.current;

      if (diff > 50 && diff < 800) {
        // 2º toque efetuado -> Destrava!
        lastUnlockClickRef.current = 0;
        setUnlockTapCount(0);
        setIsTouchLocked(false);
        setIsUnlockUIVisible(false);
        showLockedBanner('TELA DESBLOQUEADA 🔓');
        showToast('Tela Desbloqueada 🔓', 'info');
        pokePlayerControls();

        if (typeof window !== 'undefined' && window.screen && window.screen.orientation?.unlock) {
          window.screen.orientation.unlock();
        }
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try {
            navigator.vibrate([40, 40, 70]);
          } catch {}
        }
      } else {
        // 1º toque -> Registra e aguarda 2º toque (fica verde!)
        lastUnlockClickRef.current = now;
        setUnlockTapCount(1);
        showUnlockButtonTemporarily();

        if (tapResetTimerRef.current) clearTimeout(tapResetTimerRef.current);
        tapResetTimerRef.current = setTimeout(() => {
          setUnlockTapCount(0);
          lastUnlockClickRef.current = 0;
        }, 800);
      }
    },
    [pokePlayerControls, showLockedBanner, showToast, showUnlockButtonTemporarily]
  );

  // Toggle play/pause
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      safePlay();
      requestWakeLock();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      releaseWakeLock();
      setIsPlaying(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    videoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
    showToast(nextMuted ? 'Áudio silenciado' : 'Áudio ativado', 'info');
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    requestWakeLock();
    toggleVideoFullscreen(containerRef.current, videoRef.current);
  };

  // Aspect ratio switch
  const cycleAspectRatio = () => {
    const next = aspectRatio === 'contain' ? 'cover' : aspectRatio === 'cover' ? 'fill' : 'contain';
    setAspectRatio(next);
    const label = next === 'contain' ? 'Padrão (16:9)' : next === 'cover' ? 'Zoom (Preencher Tela)' : 'Esticado (100%)';
    showToast(`Proporção: ${label}`, 'info');
  };

  // Listeners de evento para Picture-in-Picture
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onEnterPip = () => setIsPipActive(true);
    const onLeavePip = () => setIsPipActive(false);
    const onPresentationChange = () => {
      const mode = (video as any).webkitPresentationMode;
      if (mode) setIsPipActive(mode === 'picture-in-picture');
    };

    video.addEventListener('enterpictureinpicture', onEnterPip);
    video.addEventListener('leavepictureinpicture', onLeavePip);
    video.addEventListener('webkitpresentationmodechanged', onPresentationChange);

    return () => {
      video.removeEventListener('enterpictureinpicture', onEnterPip);
      video.removeEventListener('leavepictureinpicture', onLeavePip);
      video.removeEventListener('webkitpresentationmodechanged', onPresentationChange);
    };
  }, []);

  // Picture in Picture (com suporte universal W3C e Safari WebKit iOS)
  const togglePiP = async () => {
    try {
      const video = videoRef.current;
      if (!video) return;

      const v = video as any;

      // Limitação conhecida da Apple: PiP é desativado pelo WebKit no modo instalado (tela inicial) no iOS.
      // Não há workaround via JS — orienta o usuário a usar o Safari normal.
      if (isStandaloneIOS) {
        showToast('Janela Flutuante indisponível no app instalado. Abra pelo Safari para usar esse recurso.', 'info');
        return;
      }

      // 0. App Android nativo (ponte pronta em MainActivity.java)
      if (typeof window !== 'undefined' && (window as any).AndroidBridge?.enterPictureInPicture) {
        try {
          (window as any).AndroidBridge.enterPictureInPicture();
          setIsPipActive(true);
          return;
        } catch (err) {
          console.warn('[PiP] AndroidBridge.enterPictureInPicture falhou:', err);
        }
      }

      // 1. W3C Standard (Chrome, Edge, Firefox, Android, Safari desktop moderno)
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPipActive(false);
        return;
      }

      // 2. Safari iOS (iPad / Mac / iPhone) via webkitSetPresentationMode
      if (typeof v.webkitSetPresentationMode === 'function') {
        try {
          const currentMode = v.webkitPresentationMode;
          const targetMode = currentMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture';
          v.webkitSetPresentationMode(targetMode);
          setIsPipActive(targetMode === 'picture-in-picture');
          return;
        } catch {}
      }

      if (video.requestPictureInPicture) {
        await video.requestPictureInPicture();
        setIsPipActive(true);
        return;
      }

      showToast('Janela Flutuante (PiP) não suportada neste navegador', 'warning');
    } catch (err: any) {
      console.warn('PiP error', err);
      // Erros de usuário (ex: cancelamento) não precisam de toast de alerta invasivo
      if (err?.name !== 'NotAllowedError') {
        showToast('Não foi possível ativar Janela Flutuante', 'warning');
      }
    }
  };

  // Reconnect / Destravar Vídeo
  const handleReconnect = () => {
    showToast('Recarregando e destravando vídeo...', 'info');
    setIsBuffering(true);
    setHasError(false);
    if (videoRef.current) {
      const savedTime = videoRef.current.currentTime || currentTime || 0;
      videoRef.current.load();
      const onLoaded = () => {
        if (videoRef.current) {
          if (savedTime > 0) {
            videoRef.current.currentTime = savedTime;
          }
          safePlay();
        }
        videoRef.current?.removeEventListener('loadedmetadata', onLoaded);
      };
      videoRef.current.addEventListener('loadedmetadata', onLoaded);
      setTimeout(() => {
        if (videoRef.current && videoRef.current.paused) {
          if (savedTime > 0 && Math.abs(videoRef.current.currentTime - savedTime) > 2) {
            videoRef.current.currentTime = savedTime;
          }
          safePlay();
        }
      }, 1000);
    }
  };

  // Compartilhar Tela / Transmitir na TV
  const handleCast = () => {
    setIsCastModalOpen(true);
  };

  if (!contentInfo) return null;

  const progressPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;

  return (
    <div
      ref={containerRef}
      onMouseMove={isTouchLocked ? undefined : handleUserActivity}
      onClick={(e) => {
        if (Date.now() - lastTouchTimeRef.current < 700) return;
        if (isTouchLocked) {
          showUnlockButtonTemporarily();
        } else {
          handleToggleControls(e);
        }
      }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center select-none overflow-hidden"
      style={{ touchAction: 'manipulation' }}
    >
      {/* HTML5 Native Video with HTTP 206 Range seeking */}
      <video
        ref={videoRef}
        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        playsInline
        autoPlay
        muted={isMuted}
        {...{
          'webkit-playsinline': 'true',
          'x-webkit-airplay': 'allow',
          airplay: 'allow',
          disablePictureInPicture: false,
        }}
        style={{
          objectFit: aspectRatio === 'fill' ? 'fill' : aspectRatio === 'cover' ? 'cover' : 'contain',
        }}
        className="w-full h-full pointer-events-none"
      />

      {/* Camada Transparente Capturadora de Toques (Garante resposta instantânea ao toque sem interferência do vídeo) */}
      {!isTouchLocked && (
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          style={{ touchAction: 'manipulation' }}
          onTouchStart={(e) => {
            lastTouchTimeRef.current = Date.now();
            enableScreenWakeLock();
            if (e.touches.length === 1) {
              touchStartPosRef.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
                time: Date.now(),
              };
            }
          }}
          onTouchEnd={(e) => {
            lastTouchTimeRef.current = Date.now();
            enableScreenWakeLock();
            if (touchStartPosRef.current && e.changedTouches.length === 1) {
              const dx = Math.abs(e.changedTouches[0].clientX - touchStartPosRef.current.x);
              const dy = Math.abs(e.changedTouches[0].clientY - touchStartPosRef.current.y);
              const dt = Date.now() - touchStartPosRef.current.time;
              touchStartPosRef.current = null;
              if (dt < 450 && dx < 25 && dy < 25) {
                e.preventDefault();
                e.stopPropagation();
                handleToggleControls(e);
              }
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            enableScreenWakeLock();
            if (Date.now() - lastTouchTimeRef.current < 700) return;
            handleToggleControls(e);
          }}
        />
      )}

      {/* Camada Invisível de Bloqueio Anti-Toque */}
      {isTouchLocked && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            showUnlockButtonTemporarily();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            showUnlockButtonTemporarily();
          }}
          className="absolute inset-0 z-40 bg-transparent cursor-default pointer-events-auto select-none"
        >
          {/* Banner rápido TELA BLOQUEADA / DESBLOQUEADA */}
          {lockedBannerText && (
            <div
              className="absolute left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-in fade-in zoom-in-95 duration-200"
              style={{ top: 'max(1.5rem, env(safe-area-inset-top, 1.5rem))' }}
            >
              <div className="px-4 py-2 rounded-xl bg-zinc-950/90 text-amber-300 border border-amber-500/40 shadow-2xl backdrop-blur-md font-bold text-xs flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <span>{lockedBannerText}</span>
              </div>
            </div>
          )}

          {/* Botão Cadeado Ampliado para Fácil Toque/Clique (1 Toque fica Verde, 2 Toques Destrava) */}
          {isUnlockUIVisible && (
            <div
              className="absolute right-4 sm:right-6 z-50 flex items-center gap-2 sm:gap-3 animate-in fade-in duration-150"
              style={{ top: 'max(1.25rem, env(safe-area-inset-top, 1.25rem))' }}
            >
              {unlockTapCount > 0 ? (
                <span className="px-3 py-1.5 rounded-xl bg-emerald-950/95 text-emerald-300 border border-emerald-400/60 text-xs font-black shadow-2xl backdrop-blur-md animate-pulse">
                  Toque mais 1x para destravar!
                </span>
              ) : (
                <span className="hidden sm:inline-block px-3 py-1.5 rounded-xl bg-black/80 text-amber-300 border border-amber-500/40 text-xs font-bold shadow-lg backdrop-blur-md">
                  Toque 2x para destravar
                </span>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnlockTap(e);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                className={`w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center cursor-pointer transition-all backdrop-blur-xl shadow-2xl ${
                  unlockTapCount > 0
                    ? 'bg-emerald-500 text-black border-4 border-emerald-300 scale-110 shadow-emerald-500/60 ring-4 ring-emerald-400/40 animate-pulse'
                    : 'bg-black/85 hover:bg-black text-amber-300 border-2 border-amber-500/60 shadow-amber-500/30 active:scale-95'
                }`}
                title="Toque 2x no cadeado para destravar"
              >
                {unlockTapCount > 0 ? (
                  <Unlock className="w-5 h-5 sm:w-7 sm:h-7 text-black stroke-[2.5]" />
                ) : (
                  <Lock className="w-5 h-5 sm:w-7 sm:h-7 text-amber-400 stroke-[2.5]" />
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* TEVEZINHA RETRÔ OPEN TV DE CARREGAMENTO */}
      {isBuffering && !hasError && (
        <TvLoadingOverlay
          title={contentInfo.title}
          image={contentInfo.image}
          type="vod"
          status="Carregando..."
          subtitle="Conectando e iniciando reprodução..."
        />
      )}

      {/* Error Overlay */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 backdrop-blur-md p-6 z-30 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-4 shadow-xl">
            <Shield className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Falha na Reprodução</h3>
          <p className="text-sm text-zinc-400 max-w-md mb-6">{errorMessage}</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (videoRef.current) {
                  setHasError(false);
                  setIsBuffering(true);
                  videoRef.current.load();
                  safePlay();
                }
              }}
              className="px-5 py-2.5 rounded-xl bg-theme-accent hover:bg-theme-accent/90 text-black font-black text-sm shadow-lg transition cursor-pointer"
            >
              Tentar Novamente
            </button>
            <button
              onClick={stopPlayback}
              className="px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-sm transition cursor-pointer"
            >
              Voltar ao Catálogo
            </button>
          </div>
        </div>
      )}

      {/* TOP BAR / OSD */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          lastTouchTimeRef.current = Date.now();
          e.stopPropagation();
        }}
        className={`absolute top-0 left-0 right-0 px-3 sm:px-6 pb-3 sm:pb-6 bg-gradient-to-b from-black/95 via-black/60 to-transparent transition-all duration-300 z-40 flex items-center justify-between gap-2 ${
          !isTouchLocked && (isPlayerControlsVisible || isBuffering)
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
        style={{
          paddingTop: 'max(1.5rem, env(safe-area-inset-top, 1.5rem))',
        }}
      >
        {/* Title and Back */}
        <div className="flex items-center gap-2 sm:gap-3.5 flex-1 min-w-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              stopPlayback();
            }}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-2 sm:p-2.5 rounded-xl sm:rounded-2xl bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition cursor-pointer shrink-0"
            title="Voltar ao catálogo"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <h2 className="text-white font-bold text-sm sm:text-base md:text-lg truncate flex-1 min-w-0">
                {contentInfo.title}
              </h2>
            </div>
            <p className="text-xs text-zinc-400 font-medium mt-0.5 truncate">
              {contentInfo.subtitle}
            </p>
          </div>
        </div>

        {/* Top Right Utilities: Bloquear Toque / Cadeado */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleLockScreen(e);
            }}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-2 sm:p-2.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/35 border border-amber-500/50 text-amber-300 hover:text-amber-200 backdrop-blur-md transition cursor-pointer shadow-md active:scale-95 flex items-center justify-center min-w-[34px] min-h-[34px] shrink-0"
            title="Bloquear Toques na Tela"
          >
            <Lock className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
          </button>
        </div>
      </div>

      {/* BOTTOM CONTROLS & TIMELINE SCRUBBER */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          lastTouchTimeRef.current = Date.now();
          e.stopPropagation();
        }}
        className={`absolute bottom-0 left-0 right-0 px-2.5 sm:px-4 md:px-6 pt-3 sm:pt-4 bg-gradient-to-t from-black/95 via-black/75 to-transparent transition-all duration-300 z-40 ${
          !isTouchLocked && (isPlayerControlsVisible || isBuffering)
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{
          paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))',
        }}
      >
        {/* Timeline Scrubber */}
        <div className="mb-2 sm:mb-3">
          <div className="flex items-center justify-between text-[11px] sm:text-xs text-zinc-400 font-mono mb-1">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-theme-accent font-bold tracking-wide">{formatSeconds(currentTime)}</span>
              {currentTime > 15 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (videoRef.current) {
                      videoRef.current.currentTime = 0;
                      setCurrentTime(0);
                    }
                  }}
                  onTouchStart={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-[10px] sm:text-[11px] font-sans text-theme-accent transition cursor-pointer shrink-0"
                  title="Assistir do início"
                >
                  <RotateCcw className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                  <span>Início</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-[10px] sm:text-[11px] text-theme-accent font-sans font-bold bg-theme-accent-subtle px-1.5 sm:px-2 py-0.5 rounded-md border border-theme-accent-subtle">
                {Math.round(progressPercent)}%
              </span>
              <span className="text-zinc-300">{formatSeconds(duration)}</span>
            </div>
          </div>

          <div className="relative group w-full py-1 flex items-center cursor-pointer">
            <input
              type="range"
              min="0"
              max={duration || 100}
              step="0.5"
              value={currentTime}
              aria-label="Barra de progresso"
              onPointerDown={(e) => {
                e.stopPropagation();
                setIsScrubbing(true);
                clearPlayerControlsTimer();
              }}
              onPointerUp={(e) => {
                e.stopPropagation();
                setIsScrubbing(false);
                handleUserActivity();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                setIsScrubbing(true);
                clearPlayerControlsTimer();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                setIsScrubbing(false);
                handleUserActivity();
              }}
              onChange={(e) => {
                const target = parseFloat(e.target.value);
                setCurrentTime(target);
                if (videoRef.current) {
                  videoRef.current.currentTime = target;
                }
              }}
              style={{
                accentColor: 'var(--theme-accent, #f59e0b)',
                background:
                  duration > 0 && bufferedSeconds > currentTime
                    ? `linear-gradient(to right, var(--theme-accent, #f59e0b) 0%, var(--theme-accent, #f59e0b) ${progressPercent}%, #71717a ${progressPercent}%, #71717a ${Math.min(100, Math.max(0, (bufferedSeconds / duration) * 100))}%, #27272a ${Math.min(100, Math.max(0, (bufferedSeconds / duration) * 100))}%, #27272a 100%)`
                    : `linear-gradient(to right, var(--theme-accent, #f59e0b) 0%, var(--theme-accent, #f59e0b) ${progressPercent}%, #27272a ${progressPercent}%, #27272a 100%)`,
              }}
              className="w-full h-2 sm:h-2.5 rounded-full appearance-none cursor-pointer hover:h-3 transition-all shadow-inner focus:outline-none focus:ring-2 ring-theme-accent"
            />
          </div>
        </div>

        {/* Primary Controls Row */}
        <div className="flex items-center justify-between gap-1 sm:gap-3 max-w-full overflow-x-auto scrollbar-none">
          {/* Left: Play/Pause, Prev/Next Ep, Mute, Reconnect */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3 shrink-0">
            {/* Prev Episode (Series) */}
            {contentInfo.type === 'series' && (
              <button
                type="button"
                disabled={!prevEpisode}
                onClick={handlePlayPrevEpisode}
                onTouchStart={(e) => e.stopPropagation()}
                className={`w-9 h-9 sm:w-10 sm:h-10 p-1.5 sm:p-2.5 rounded-xl sm:rounded-xl transition flex items-center justify-center shrink-0 ${
                  prevEpisode
                    ? 'bg-zinc-800/90 hover:bg-zinc-700 active:scale-95 text-zinc-200 hover:text-white cursor-pointer'
                    : 'bg-zinc-900/40 text-zinc-600 cursor-not-allowed opacity-30'
                }`}
                title={
                  prevEpisode
                    ? `Episódio Anterior: ${prevEpisode.title || `T${prevEpisode.seasonNumber}E${prevEpisode.episodeNumber}`}`
                    : 'Não há episódio anterior'
                }
              >
                <SkipBack className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Play / Pause */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
                pokePlayerControls();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-11 h-11 sm:w-12 sm:h-12 p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-theme-accent hover:opacity-90 text-black shadow-lg shadow-theme-accent transition transform active:scale-95 cursor-pointer flex items-center justify-center shrink-0"
              title={isPlaying ? 'Pausar (Espaço)' : 'Reproduzir (Espaço)'}
            >
              {isPlaying ? <Pause className="w-5 h-5 sm:w-5 sm:h-5 fill-black text-black" /> : <Play className="w-5 h-5 sm:w-5 sm:h-5 fill-black text-black ml-0.5" />}
            </button>

            {/* Next Episode (Series) */}
            {contentInfo.type === 'series' && (
              <button
                type="button"
                disabled={!nextEpisode}
                onClick={handlePlayNextEpisode}
                onTouchStart={(e) => e.stopPropagation()}
                className={`w-9 h-9 sm:w-10 sm:h-10 p-1.5 sm:p-2.5 rounded-xl sm:rounded-xl transition flex items-center justify-center shrink-0 ${
                  nextEpisode
                    ? 'bg-zinc-800/90 hover:bg-zinc-700 active:scale-95 text-zinc-200 hover:text-white cursor-pointer'
                    : 'bg-zinc-900/40 text-zinc-600 cursor-not-allowed opacity-30'
                }`}
                title={
                  nextEpisode
                    ? `Próximo Episódio: ${nextEpisode.title || `T${nextEpisode.seasonNumber}E${nextEpisode.episodeNumber}`}`
                    : 'Último episódio disponível'
                }
              >
                <SkipForward className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Mute */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Silenciar / Ativar Áudio (M)"
            >
              {isMuted ? <VolumeX className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-red-400" /> : <Volume2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" />}
            </button>

            {/* Recarregar / Destravar */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleReconnect();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Recarregar / Destravar vídeo"
            >
              <RefreshCw className={`w-3.5 h-3.5 sm:w-5 sm:h-5 ${isBuffering ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Right: Diagnóstico (Admin), PiP, Cast, Fullscreen */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Diagnóstico Técnico / Telemetria — V8.5.1: só a conta MARIOZELI (admin/desenvolvedor) */}
            {canSeeTelemetry && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDiagnosticModal(true);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-500/40 text-emerald-300 transition cursor-pointer flex items-center justify-center shrink-0"
                title="Diagnóstico de Reprodução (Admin)"
              >
                <Activity className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Picture in Picture (oculto no modo instalado do iOS, onde o WebKit não permite PiP) */}
            {!isStandaloneIOS && (typeof document !== 'undefined' && (
              document.pictureInPictureEnabled ||
              (typeof window !== 'undefined' && !!(window as any).AndroidBridge?.enterPictureInPicture) ||
              (typeof HTMLVideoElement !== 'undefined' && (
                'webkitSetPresentationMode' in HTMLVideoElement.prototype ||
                'webkitSupportsPresentationMode' in HTMLVideoElement.prototype
              )) ||
              (videoRef.current && (videoRef.current as any).webkitSupportsPresentationMode?.('picture-in-picture')) ||
              (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent))
            )) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePiP();
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className={`w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl transition cursor-pointer flex items-center justify-center shrink-0 ${
                  isPipActive
                    ? 'bg-theme-accent text-white shadow-lg ring-2 ring-white/50'
                    : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white'
                }`}
                title={isPipActive ? "Sair da Janela Flutuante (PiP)" : "Picture-in-Picture (Janela Flutuante)"}
              >
                <SquareDashedBottom className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Compartilhar tela / Cast */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCast();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Transmitir na Smart TV (Open TV Connection)"
            >
              <Cast className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
            </button>

            {/* Fullscreen / Maximizar (Ativa player nativo no iOS e previne bloqueio de tela) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Tela Cheia (F)"
            >
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" /> : <Maximize2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* CARD FLUTUANTE DE PRÓXIMO EPISÓDIO (40 SEGUNDOS ANTES DO FIM) */}
      {(() => {
        const remainingSeconds = duration > 0 ? Math.max(0, Math.floor(duration - currentTime)) : 999;
        const showNextEpisodeCard =
          contentInfo?.type === 'series' &&
          Boolean(nextEpisode) &&
          duration > 15 &&
          remainingSeconds <= 45 &&
          remainingSeconds > 0 &&
          !isNextEpCardDismissed &&
          !isTouchLocked;

        if (!showNextEpisodeCard || !nextEpisode) return null;

        const countdownPercent = Math.min(100, Math.max(0, ((40 - remainingSeconds) / 40) * 100));

        return (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute right-3 sm:right-6 left-3 sm:left-auto z-40 max-w-sm sm:w-80 bg-zinc-950/95 border border-amber-500/50 rounded-2xl p-3 sm:p-3.5 shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-bottom-4 duration-300 select-none overflow-hidden"
            style={{
              bottom: 'max(6.5rem, calc(env(safe-area-inset-bottom, 0px) + 6rem))',
            }}
          >
            {/* Header do Card */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 text-amber-400 font-black text-xs uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 fill-current" />
                <span>Próximo Episódio em {remainingSeconds}s</span>
              </div>
              <button
                onClick={() => setIsNextEpCardDismissed(true)}
                onTouchStart={(e) => e.stopPropagation()}
                className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/80 transition cursor-pointer"
                title="Dispensar aviso"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Conteúdo com Thumbnail e Título */}
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-12 rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 shrink-0">
                {(nextEpisode.thumbnail || contentInfo.image) ? (
                  <img
                    src={nextEpisode.thumbnail || contentInfo.image}
                    alt={nextEpisode.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600">
                    <Play className="w-5 h-5 fill-current" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-bold text-amber-300 block truncate">
                  T{nextEpisode.seasonNumber}:E{nextEpisode.episodeNumber}
                </span>
                <h4 className="text-xs font-bold text-white truncate">
                  {nextEpisode.title || `Episódio ${nextEpisode.episodeNumber}`}
                </h4>
              </div>
            </div>

            {/* Botão de Ação Imediata */}
            <button
              type="button"
              onClick={handlePlayNextEpisode}
              onTouchStart={(e) => {
                e.stopPropagation();
                handlePlayNextEpisode(e);
              }}
              className="w-full mt-2.5 py-2 px-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-extrabold text-xs flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition cursor-pointer active:scale-95"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Assistir Agora</span>
            </button>

            {/* Barra de Progresso Regressiva dos 40 Segundos */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800/80">
              <div
                className="h-full bg-amber-400 transition-all duration-500 ease-linear"
                style={{ width: `${countdownPercent}%` }}
              />
            </div>
          </div>
        );
      })()}

      {/* Cast Modal (Chromecast & AirPlay) */}
      {isCastModalOpen && (
        <CastModal
          isOpen={isCastModalOpen}
          onClose={() => setIsCastModalOpen(false)}
          videoElement={videoRef.current}
          streamTitle={contentInfo.title}
          directStreamUrl={contentInfo.rawUrl}
          proxiedStreamUrl={streamUrl}
          streamUrl={contentInfo.rawUrl}
          streamType={contentInfo.type}
        />
      )}

      {/* Modal de Diagnóstico Técnico (Permanente conforme AGENTS.md) */}
      {/* Painel de diagnóstico (o mesmo do Stick): rede, latência, memória, armazenamento, capas, vídeo + registros */}
      <DiagnosticsPanel
        open={showDiagnosticModal}
        onClose={() => setShowDiagnosticModal(false)}
        videoElement={videoRef.current}
        logs={diagnosticLogs}
        title="Diagnóstico"
        extraLines={[`Conteudo: ${currentMovie?.title || (currentSeries && currentEpisode ? `${currentSeries.title} T${currentEpisode.seasonNumber}:E${currentEpisode.episodeNumber}` : '-')}`]}
      />
    </div>
  );
};
